import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TaskStatus, type Task, type TaskLog } from "@/contracts/tasks";
import { api } from "@/lib/api";
import { taskKeys, TASK_LIST_STALE_TIME } from "./query-keys";
import { mergeTaskLogs, taskLogFromDate, TASK_LOG_SETTLE_TIME } from "./task-log-polling";

export function useTasks() {
  return useQuery({
    queryKey: taskKeys.all,
    queryFn: api.tasks.fetchTasks,
    staleTime: TASK_LIST_STALE_TIME,
    refetchIntervalInBackground: false,
    refetchInterval: (query) =>
      query.state.data?.some((task) => task.status === TaskStatus.Running) ? 2_000 : 15_000,
  });
}

export function useTaskLogs(task: Task, live: boolean) {
  const queryClient = useQueryClient();
  // A new run or completion gets a fresh snapshot, including any late writes.
  const queryKey = [...taskKeys.logs(task.id), task.startedAt, task.completedAt];
  const active = task.status === TaskStatus.Running;

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const previous = queryClient.getQueryData<TaskLog[]>(queryKey) ?? [];
      const fromDate = active && !queryClient.getQueryState(queryKey)?.isInvalidated
        ? taskLogFromDate(previous)
        : undefined;
      let incoming = await api.tasks.fetchLogs(task.id, { fromDate });
      if (fromDate !== undefined && incoming.length && previous[0]?.executionId !== incoming[0].executionId) {
        // The endpoint always selects the latest execution. Re-fetch its full
        // history if it changed before the task-list poll caught up.
        incoming = await api.tasks.fetchLogs(task.id);
        return mergeTaskLogs([], incoming);
      }
      return mergeTaskLogs(previous, incoming);
    },
    refetchIntervalInBackground: false,
    refetchInterval: () => live && (active || (
      task.completedAt !== null && Date.now() < Date.parse(task.completedAt) + TASK_LOG_SETTLE_TIME
    )) ? 5_000 : false,
  });
  return {
    ...query,
    refresh: async () => {
      await queryClient.invalidateQueries({ queryKey, exact: true, refetchType: "none" });
      return query.refetch();
    },
  };
}
